/**
 * Stripe Configuration Validator
 *
 * This utility validates all required Stripe environment variables at application startup
 * and provides helpful error messages for missing variables.
 */

// Subscription product types
const SUBSCRIPTION_TYPES = [
  { id: 'verified-mark-certificate', prefix: 'STRIPE_VMC' },
  { id: 'government-mark-certificate', prefix: 'STRIPE_GMC' },
  { id: 'common-mark-certificate', prefix: 'STRIPE_CMC' },
  { id: 'modified-registered-mark', prefix: 'STRIPE_MRM' },
];

// Subscription periods
const PERIODS = [1, 2, 3];

/**
 * Validates all required Stripe environment variables
 * @returns {Object} Validation result with missing variables and warnings
 */
function validateStripeConfig() {
  const missingVars = [];
  const warnings = [];

  // Check for critical Stripe API keys
  if (!process.env.STRIPE_SECRET_KEY) {
    missingVars.push('STRIPE_SECRET_KEY');
  }

  if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
    missingVars.push('NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY');
  }

  // Check all subscription-related variables
  SUBSCRIPTION_TYPES.forEach((type) => {
    PERIODS.forEach((period) => {
      const priceVar = `${type.prefix}_${period}_YEAR_PRICE`;
      const productVar = `${type.prefix}_${period}_YEAR_PRODUCT`;

      if (!process.env[priceVar]) {
        missingVars.push(priceVar);
      }

      if (!process.env[productVar]) {
        missingVars.push(productVar);
      }
    });

    // Check for legacy variables and warn
    const legacyVar = `${type.prefix}_PRICE`;
    if (process.env[legacyVar]) {
      warnings.push(
        `Legacy variable ${legacyVar} is still in use. Please update to year-specific variables.`
      );
    }
  });

  return {
    isValid: missingVars.length === 0,
    missingVars,
    warnings,
    criticalIssue:
      missingVars.includes('STRIPE_SECRET_KEY') ||
      missingVars.includes('NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY'),
  };
}

/**
 * Logs the validation results in a friendly format
 * @param {Object} result - Validation result from validateStripeConfig
 */
function logValidationResults(result) {
  console.log('==========================================');
  console.log('🔍 STRIPE CONFIGURATION VALIDATION');
  console.log('==========================================');

  if (result.isValid && result.warnings.length === 0) {
    console.log('✅ All Stripe configuration variables are valid');
  } else {
    /* if (result.missingVars.length > 0) {
      console.log('\n❌ Missing required environment variables:');
      result.missingVars.forEach((variable) => {
        console.log(`   - ${variable}`);
      });
    } */

    if (result.warnings.length > 0) {
      console.log('\n⚠️ Warnings:');
      result.warnings.forEach((warning) => {
        console.log(`   - ${warning}`);
      });
    }

    if (result.criticalIssue) {
      console.log('\n🚨 CRITICAL ISSUE: Missing Stripe API keys. Payment processing will fail!');
    }
  }

  console.log('==========================================');
}

/**
 * Validates Stripe configuration and logs results
 * Should be called during application startup
 * @returns {boolean} True if configuration is valid
 */
function validateStripeConfigAndLog() {
  const result = validateStripeConfig();
  logValidationResults(result);
  return result.isValid;
}

// Run validation if this file is executed directly
if (typeof module !== 'undefined' && require.main === module) {
  validateStripeConfigAndLog();
}

// Use consistent ES Module exports for Next.js
export { logValidationResults, validateStripeConfig, validateStripeConfigAndLog };

// Make validateStripeConfigAndLog the default export
export default validateStripeConfigAndLog;
